import { IOrderClause } from '@tableau/catalog-ui-temp';
import { DataGrid, IndexBasedGridSelection } from '@tableau/widgets-datagrid';
import React from 'react';
import { Dictionary } from 'underscore';

import { Messages } from '~src/codegen/Localize';
import { PlainTextCell } from '~src/spacesPlatform/components/ListView/dataGridCells/PlainTextAndNumberCells';
import { SortableHeader } from '~src/spacesPlatform/components/ListView/SortableHeader';
import { CommonColumn } from '~src/spacesPlatform/components/ListView/types/Columns';

import { IAuthenticationProvider, ISiteAuthenticationSettings, SiteAuthenticationSettingsService } from '../../services/SiteAuthenticationSettingsService';
import { authenticationDataGridStyle } from '../../styles/SiteAuthenticationSettingsStyles';
import { AuthenticationActionCellOptions, AuthenticationActionsMenuCell } from './AuthenticationActionsCells';

export interface AuthenticationGridRow extends DataGrid.Row {

}

export enum AuthenticationColumnKeys {
  AuthenticationName = 'name',
  Status = 'status',
  AuthenticationType = 'authenticationType',
  AuthenticationProvider = 'authenticationProvider',
}

export type AuthenticationGridProps = {
    displayData: IAuthenticationProvider[];
    currentColumnSortOrder: IOrderClause;
    handleColumnSortOrderChange: (field: string) => void;
    isExternalProviderEnabledChanged: () => void;
}

export type AuthenticationGridState = {
  allProviders: IAuthenticationProvider[];
}

interface AuthenticationGridRowAndColumnInfo {
    cols: DataGrid.Column[];
    rows: DataGrid.Row[];
    rowStore: DataGrid.RowStore;
  }

// A grid based off of ListView component (see ListViewColumnGenerator, ListViewContentGenerator too).
// ListView is connected to the Redux store, and Authentication Settings are pure components (non-redux).
// Additionally, ListView must be populated with objects of type ContentType, which might take more wiring if we want to use that.
export class AuthenticationTypesGrid extends React.Component<AuthenticationGridProps, AuthenticationGridState> {
    private readonly minColWidth: number = 32;
    private readonly selectionModel: IndexBasedGridSelection = new IndexBasedGridSelection(0, 0);

    constructor(props: AuthenticationGridProps) {
      super(props);
      this.state = {
        allProviders: []
      };
    }

    public componentDidMount(): void {
        this.setupInitialState();
    }

    private setupInitialState(refresh: boolean = false) {
      SiteAuthenticationSettingsService.getSiteAuthenticationSettings(refresh).then((siteAuthSettings: ISiteAuthenticationSettings) => {
        this.setState({ allProviders: siteAuthSettings.allProviders });
      });
    }

    private createGroupsGridRowAndColumnInfo(): AuthenticationGridRowAndColumnInfo {
        let cols = this.generateColumns();
        let rows = this.generateRows();
        let rowStore = this.generateRowStore(rows);
        let groupsGridRowAndColumnInfo = {rows: rows, cols: cols, rowStore: rowStore};
        return groupsGridRowAndColumnInfo;
      }

    // Generates the templates for each column within the grid.
    private generateColumns(): DataGrid.Column[] {
        const sortableHeaderFormatter = SortableHeader.Formatter([this.props.currentColumnSortOrder], this.props.handleColumnSortOrderChange);

        // const sortableHeaderFormatterWithOverrides = (styleOverrides?: React.CSSProperties) => {
        //     return SortableHeader.Formatter(this.params.currentSort, this.params.changeSort, undefined, styleOverrides);
        // };
        const columns: DataGrid.Column[] = [];

        columns.push({
            name: AuthenticationColumnKeys.AuthenticationName,
            dataKey: AuthenticationColumnKeys.AuthenticationName,
            sortable: true,
            headerText: Messages.name(),
            direction: 'asc',
            growToFit: true,
            headerFormatter: sortableHeaderFormatter,
            cellFormatter: PlainTextCell(AuthenticationColumnKeys.AuthenticationName)
        });
        columns.push({
            name: CommonColumn.ActionsMenu,
            dataKey: CommonColumn.ActionsMenu,
            headerText: Messages.actions(),
            sortable: false,
            resizable: false,
            headerFormatter: sortableHeaderFormatter,
            cellFormatter: AuthenticationActionsMenuCell(this.generateActionsOptions())
        });
        columns.push({
            name: AuthenticationColumnKeys.Status,
            dataKey: AuthenticationColumnKeys.Status,
            headerText: Messages.status(),
            headerFormatter: sortableHeaderFormatter,
            cellFormatter: PlainTextCell(AuthenticationColumnKeys.Status)
        });
        columns.push({
            name: AuthenticationColumnKeys.AuthenticationType,
            dataKey: AuthenticationColumnKeys.AuthenticationType,
            headerText: Messages.ssoAuthenticationType(),
            sortable: false,
            resizable: false,
            headerFormatter: sortableHeaderFormatter,
            cellFormatter: PlainTextCell(AuthenticationColumnKeys.AuthenticationType)
        });
        return columns;
    }


    private generateActionsOptions(): AuthenticationActionCellOptions {
        return {
          getProviderInfo: (row: DataGrid.Row) => row.data as Dictionary<any>,
          disableAuthenticationProvider: () => { return Promise.resolve(); },
          refreshGrid: () => { this.setupInitialState(); },
        };
    }

    // Creates an object that represents each row and its corresponding data.  To add more information for each row,
    // add to object rowItemData
    private generateRows(): DataGrid.Row[] {
        let rows: AuthenticationGridRow[] = [];
        let nextRowIndex: number = 0;
        this.state.allProviders?.forEach(authenticationProvider => {
          let rowItemData: { [key: string]: {} } = {};
          const currentRowIndex = nextRowIndex++;

          if (authenticationProvider) {
            // TODO: We need to add the custom provider name here so that we can display it within the grid
            rowItemData[AuthenticationColumnKeys.AuthenticationName] = authenticationProvider.idpConfigurationName;
            // rowItemData[CommonColumn.ActionsMenu] = 
            rowItemData[AuthenticationColumnKeys.Status] = authenticationProvider.isEnabled ? Messages.enabled() : Messages.disabled();
            rowItemData[AuthenticationColumnKeys.AuthenticationType] = authenticationProvider.alias ? authenticationProvider.alias : authenticationProvider.type;
            rowItemData[AuthenticationColumnKeys.AuthenticationProvider] = authenticationProvider;
          }
          rows.push({
              key: (currentRowIndex).toString(),
              data: rowItemData
          });
        });

        return rows;
    }

    private generateRowStore(rows: DataGrid.Row[]): DataGrid.RowStore {
        return {
          getItems: (start: number, end: number, rowHandler: (rows: DataGrid.Row[]) => void) => {
            rowHandler(rows);
          }
        };
    }

    private emptyChild = (): JSX.Element => {
        return (
          <div className={'tb-data-grid-empty'}>
            <div className={'tb-data-grid-empty-title'}>{Messages.noMatchingResults()}</div>
          </div>
        );
    };

    public render(): JSX.Element {
        const rowAndColumnDataGridInfo = this.createGroupsGridRowAndColumnInfo();

        const dataGridProps: DataGrid.Props = {
          cols: rowAndColumnDataGridInfo.cols,
          config: {
            rowOuterHeight: this.minColWidth,
            rememberLastScrollPos: true
          },
          emptyChild: this.emptyChild(),
          rowStore: rowAndColumnDataGridInfo.rowStore,
          selectionModel: this.selectionModel,
          showHeaders: true,
          showVerticalGridLines: false,
          showHorizontalGridLines: true,
          showRowBanding: false,
          style: authenticationDataGridStyle,
        };

        return (
            <div className={'tb-spaces-list-view'} data-tb-test-id="tb-groups-data-grid">
                <DataGrid {...dataGridProps}/>
            </div>
        );
    }
}